import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRightRail } from '../../contexts/RightRailContext';
import { apiCall } from '../../config/api';
import { CalendarComponent, ChangedEventArgs } from '@syncfusion/ej2-react-calendars';
import {
  CellClickEventArgs,
  Day,
  DragEventArgs,
  EventClickArgs,
  EventRenderedArgs,
  Inject,
  PopupOpenEventArgs,
  RenderCellEventArgs,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  Week,
} from '@syncfusion/ej2-react-schedule';
import {
  BeforeOpenCloseMenuEventArgs,
  ContextMenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from '@syncfusion/ej2-react-navigations';
import { addClass, closest, isNullOrUndefined } from '@syncfusion/ej2-base';
import {
  FaCalendarAlt,
  FaUsers,
  FaClock,
  FaCalendarCheck,
  FaUserClock,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaTrash,
  FaUserMd,
  FaCalendarDay,
} from 'react-icons/fa';
import '../../styles/syncfusion.css';
import '../../styles/syncfusion-overrides.css';
import BookAppointmentModal from '../../components/BookAppointmentModal';
import MoveAppointmentModal from '../../components/MoveAppointmentModal';

interface AppointmentStats {
  total: number;
  scheduled: number;
  completed: number;
  cancelled: number;
  no_show: number;
  in_progress: number;
}

interface Appointment {
  id: string;
  patient_id?: string | number;
  patient_name: string;
  patient_mrn: string;
  provider_name: string;
  appointment_type: string;
  start_time: string;
  end_time: string;
  status: string;
  room_name?: string;
  notes?: string;
  admin_notes?: string;
  outstanding_amount?: number;
  outstanding_balance?: number;
}

interface SchedulerEvent {
  Id: string;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  Status: string;
  CategoryColor: string;
  ProviderId?: string;
  ProviderName?: string;
  DoctorId?: string;
  IsBlock?: boolean;
  IsReadonly?: boolean;
  Type?: 'appointment' | 'session';
  CssClass?: string;
  FacilityName?: string;
  RoomName?: string;
  RoomId?: string;
}

interface ProviderOption {
  id: string;
  name: string;
}

interface AppointmentTodo {
  id: string;
  text: string;
  category: 'PIF' | 'IFC' | 'Admin' | 'Billing';
  completed: boolean;
}

// Persists the calendar's view state (date, Day/Week, time interval, which
// providers' columns are shown) across an F5 reload for the tab's session --
// sessionStorage rather than localStorage, since this is a per-tab working
// view, not a saved preference that should follow the user to a new tab.
const SCHEDULER_VIEW_STORAGE_KEY = 'te_scheduler_view_state';

interface PersistedSchedulerView {
  selectedDate?: string;
  currentView?: 'Day' | 'Week';
  slotMinutes?: 10 | 15 | 30 | 60;
  visibleProviderIds?: string[];
}

const loadPersistedSchedulerView = (): PersistedSchedulerView => {
  try {
    const raw = sessionStorage.getItem(SCHEDULER_VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const AppointmentsDashboard: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const scheduleRef = useRef<ScheduleComponent | null>(null);
  const contextMenuRef = useRef<ContextMenuComponent | null>(null);
  const contextMenuTargetRef = useRef<Element | null>(null);
  const { registerButton, unregisterButton, activePanelId } = useRightRail();

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const persisted = loadPersistedSchedulerView().selectedDate;
    const parsed = persisted ? new Date(persisted) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
  });
  const [currentView, setCurrentView] = useState<'Day' | 'Week'>(() => loadPersistedSchedulerView().currentView || 'Day');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [schedulerEvents, setSchedulerEvents] = useState<SchedulerEvent[]>([]);
  const [sessionEvents, setSessionEvents] = useState<SchedulerEvent[]>([]);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [visibleProviderIds, setVisibleProviderIds] = useState<string[]>(() => loadPersistedSchedulerView().visibleProviderIds || []);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  const [contextMenuAppointment, setContextMenuAppointment] = useState<{ providerId: string; startTime: Date } | null>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [movingAppointment, setMovingAppointment] = useState<{ id: string; providerId: string; startTime: Date; durationMinutes: number } | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [slotMinutes, setSlotMinutes] = useState<10 | 15 | 30 | 60>(() => loadPersistedSchedulerView().slotMinutes || 15);
  const [todoItems, setTodoItems] = useState<AppointmentTodo[]>([]);
  const [todoText, setTodoText] = useState('');
  const [todoCategory, setTodoCategory] = useState<AppointmentTodo['category']>('Admin');
  const [stats, setStats] = useState<AppointmentStats>({
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0,
    in_progress: 0,
  });
  const [loading, setLoading] = useState(true);
  const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);

  const showSidebar = activePanelId === 'appointments-sidebar';

  // Register calendar & patient sidebar button in the right rail
  useEffect(() => {
    registerButton({
      id: 'appointments-sidebar',
      icon: <FaCalendarDay className="text-base" />,
      label: 'Calendar & Patient',
      order: 20,
    });
    return () => unregisterButton('appointments-sidebar');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formatDateWithOrdinal = (date: Date, includeWeekday = true): string => {
    const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-AU', { month: 'short' });
    const year = date.getFullYear();
    const baseDate = `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
    return includeWeekday ? `${weekday}, ${baseDate}` : baseDate;
  };

  const getWeekRangeLabel = (date: Date): string => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${formatDateWithOrdinal(start, false)} - ${formatDateWithOrdinal(end, false)}`;
  };

  const headerTitle = useMemo(() => {
    if (currentView === 'Day') return formatDateWithOrdinal(selectedDate);
    return getWeekRangeLabel(selectedDate);
  }, [currentView, selectedDate]);

  // Converts a #rrggbb hex color to rgba(...) with the given opacity, so a
  // session's real color can be applied as a plain inline style (the pattern
  // already proven reliable for appointment fills) instead of a CSS custom
  // property + color-mix(), which silently never worked for the session
  // background tint despite the CSS rule and JS both looking correct.
  const hexToRgba = (hex: string, alpha: number): string => {
    const clean = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(clean)) return hex;
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const statusToColor = (status: string) => {
    if (status === 'in_consult' || status === 'in_waiting_room') return '#3b82f6';
    if (status === 'completed') return '#10b981';
    if (status === 'cancelled') return '#ef4444';
    if (status === 'no_show') return '#f59e0b';
    return '#6b7280';
  };

  const getStatusIndicator = (statusRaw?: string): { label: string; color: string } => {
    const status = (statusRaw || '').toLowerCase();

    if (status === 'tentative') return { label: 'T', color: '#3b82f6' };
    if (status === 'scheduled' || status === 'confirmed') return { label: 'S', color: '#3b82f6' };
    if (status === 'in_waiting_room' || status === 'checked_in') return { label: 'W', color: '#f59e0b' };
    if (status === 'in_consult') return { label: 'IC', color: '#10b981' };
    if (status === 'to_be_billed') return { label: 'TB', color: '#8b5cf6' };
    if (status === 'completed') return { label: 'C', color: '#6b7280' };
    if (status === 'cancelled') return { label: 'X', color: '#ef4444' };
    if (status === 'no_show') return { label: 'N', color: '#f59e0b' };
    return { label: 'S', color: '#3b82f6' };
  };

  const getViewRange = (forDate: Date, view: 'Day' | 'Week') => {
    const start = new Date(forDate);
    start.setHours(0, 0, 0, 0);

    if (view === 'Week') {
      start.setDate(start.getDate() - start.getDay());
    }

    const end = new Date(start);
    end.setDate(start.getDate() + (view === 'Week' ? 7 : 1));

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const getDateOnlyRange = (forDate: Date, view: 'Day' | 'Week') => {
    const start = new Date(forDate);
    start.setHours(0, 0, 0, 0);

    if (view === 'Week') {
      start.setDate(start.getDate() - start.getDay());
    }

    const end = new Date(start);
    end.setDate(start.getDate() + (view === 'Week' ? 6 : 0));

    const fmt = (dateValue: Date) => {
      const yyyy = dateValue.getFullYear();
      const mm = `${dateValue.getMonth() + 1}`.padStart(2, '0');
      const dd = `${dateValue.getDate()}`.padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    return {
      startDate: fmt(start),
      endDate: fmt(end),
    };
  };

  const getFacilityCode = (facilityName?: string) => {
    if (!facilityName) return 'SITE';
    const normalized = facilityName.toLowerCase();
    if (normalized.includes('clayton')) return 'MPG';
    if (normalized.includes('monash house')) return 'MHPH';
    return facilityName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 4)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'SITE';
  };

  const formatLocalTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatLocalDateTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString([], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const loadAppointments = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const { startDate, endDate } = getViewRange(selectedDate, currentView);
      const response = await apiCall(
        `/scheduling/appointments/?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
        { method: 'GET' },
        token,
      );
      const data = await response.json();
      const appointmentsList = (data.results || data || [])
        .filter((appointment: Appointment) => {
          if (!appointment.start_time) return false;
          const start = new Date(appointment.start_time);
          return !Number.isNaN(start.getTime());
        })
        .sort((a: Appointment, b: Appointment) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
        );
      setAppointments(appointmentsList);

      setStats({
        total: appointmentsList.length,
        scheduled: appointmentsList.filter((a: Appointment) => a.status === 'scheduled').length,
        completed: appointmentsList.filter((a: Appointment) => a.status === 'completed').length,
        cancelled: appointmentsList.filter((a: Appointment) => a.status === 'cancelled').length,
        no_show: appointmentsList.filter((a: Appointment) => a.status === 'no_show').length,
        in_progress: appointmentsList.filter((a: Appointment) => a.status === 'in_consult' || a.status === 'in_waiting_room').length,
      });
    } catch (error) {
      console.error('Error loading daily appointments:', error);
      setAppointments([]);
      setStats({
        total: 0,
        scheduled: 0,
        completed: 0,
        cancelled: 0,
        no_show: 0,
        in_progress: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSchedulerEvents = async () => {
    if (!token) return;

    try {
      const { startDate, endDate } = getViewRange(selectedDate, currentView);
      const response = await apiCall(
        `/scheduling/appointments/calendar_data/?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
        { method: 'GET' },
        token,
      );
      const data = await response.json();
      const events = (Array.isArray(data) ? data : data.results || [])
        .map((item: any): SchedulerEvent | null => {
          const startRaw = item.start_time || item.start || item.start_datetime;
          const endRaw = item.end_time || item.end || item.end_datetime;
          if (!startRaw || !endRaw) return null;

          const start = new Date(startRaw);
          const end = new Date(endRaw);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

          return {
            Id: String(item.id),
            // No provider name here — the scheduler already groups appointments
            // into that doctor's own column, so repeating it on every card is redundant.
            Subject: item.patient_name || item.title || 'Appointment',
            StartTime: start,
            EndTime: end,
            Status: item.status || 'scheduled',
            // Fill color is the appointment TYPE (NC, TC, SC, individual procedure
            // codes, etc.) — each carries its own distinct color. Status is shown
            // separately via the small indicator badge in onEventRendered, same
            // as before; it no longer drives the fill color.
            CategoryColor: item.appointment_type_color || statusToColor(item.status || 'scheduled'),
            ProviderId: item.resource_id ? String(item.resource_id) : undefined,
            ProviderName: item.provider_name || undefined,
            RoomId: item.room_id ? String(item.room_id) : undefined,
            RoomName: item.room_name || undefined,
            Type: 'appointment',
          };
        })
        .filter((event: SchedulerEvent | null): event is SchedulerEvent => event !== null);

      setSchedulerEvents(events);
    } catch (error) {
      console.error('Error loading scheduler events:', error);
      setSchedulerEvents([]);
    }
  };

  const loadSessionEvents = async () => {
    if (!token) return;

    try {
      const { startDate, endDate } = getDateOnlyRange(selectedDate, currentView);
      const primaryResponse = await apiCall(
        `/providers/sessions/calendar_data/?date_from=${encodeURIComponent(startDate)}&date_to=${encodeURIComponent(endDate)}&facility=&facility_id=`,
        { method: 'GET' },
        token,
      );

      if (!primaryResponse.ok) {
        setSessionEvents([]);
        return;
      }

      const parseSessions = (payload: any) => (Array.isArray(payload) ? payload : payload.results || [])
        .map((item: any): SchedulerEvent | null => {
          const startRaw = item.start_time || item.start || item.start_datetime;
          const endRaw = item.end_time || item.end || item.end_datetime;
          if (!startRaw || !endRaw) return null;

          const start = new Date(startRaw);
          const end = new Date(endRaw);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

          const providerId = item.resource_id || item.provider_id || item.provider;

          return {
            Id: `session-${String(item.id)}`,
            Subject: '',
            StartTime: start,
            EndTime: end,
            Status: item.status || 'scheduled',
            // session_background_color is the backend's already-resolved final
            // color (ProviderSession.color override -> facility type -> facility
            // -> session type -> fallback) — the other *_color fields exist on
            // the API response only as raw inputs to that same resolution, not
            // as alternatives to re-fall-back through here.
            CategoryColor: item.session_background_color || 'rgba(59, 130, 246, 0.18)',
            ProviderId: providerId ? String(providerId) : undefined,
            ProviderName: item.provider_name || undefined,
            Type: 'session',
            IsBlock: true,
            IsReadonly: true,
            CssClass: 'te-session-block',
            FacilityName: item.facility_abbreviation || undefined,
            RoomName: item.room_name || undefined,
          };
        })
        .filter((event: SchedulerEvent | null): event is SchedulerEvent => event !== null);

      const primaryData = await primaryResponse.json();
      let sessions = parseSessions(primaryData);

      if (sessions.length === 0) {
        const fallbackResponse = await apiCall('/providers/sessions/calendar_data/?facility=&facility_id=', { method: 'GET' }, token);
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          sessions = parseSessions(fallbackData);
        }
      }

      setSessionEvents(sessions);
    } catch (error) {
      console.error('Error loading provider sessions for background layer:', error);
      setSessionEvents([]);
    }
  };

  const loadProviders = async () => {
    if (!token) return;

    try {
      const response = await apiCall('/providers/providers/?is_active=true', { method: 'GET' }, token);
      if (!response.ok) {
        setProviders([]);
        return;
      }

      const data = await response.json();
      const list = (Array.isArray(data) ? data : data.results || []).map((provider: any) => ({
        id: String(provider.id),
        name: provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'Provider',
      }));
      setProviders(list);
    } catch (error) {
      console.error('Error loading providers for calendar filter:', error);
      setProviders([]);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviders();
    loadSchedulerEvents();
    loadSessionEvents();
  }, [token, selectedDate, currentView]);

  useEffect(() => {
    if (providers.length === 0) {
      setVisibleProviderIds([]);
      return;
    }

    setVisibleProviderIds((previous) => {
      if (previous.length === 0) {
        return providers.map((provider) => provider.id);
      }
      const providerIdSet = new Set(providers.map((provider) => provider.id));
      return previous.filter((providerId) => providerIdSet.has(providerId));
    });
  }, [providers]);

  // Persists the view state above on every change so an F5 reload lands
  // back where the user left off instead of resetting to today/Day/15min.
  useEffect(() => {
    try {
      sessionStorage.setItem(SCHEDULER_VIEW_STORAGE_KEY, JSON.stringify({
        selectedDate: selectedDate.toISOString(),
        currentView,
        slotMinutes,
        visibleProviderIds,
      }));
    } catch {
      // sessionStorage unavailable (private browsing, quota) -- persistence
      // is a convenience, not required for the page to function.
    }
  }, [selectedDate, currentView, slotMinutes, visibleProviderIds]);

  const filteredSchedulerEvents = useMemo(() => {
    if (visibleProviderIds.length === 0) return [];
    const allowed = new Set(visibleProviderIds);

    const filteredAppointments = schedulerEvents
      .filter((event) => !!event.ProviderId && allowed.has(event.ProviderId) && event.Status !== 'cancelled')
      .map((event) => ({ ...event, DoctorId: event.ProviderId }));

    // Session background rendering is disabled for now (TE-21 attempted
    // isBlock events; Syncfusion allocated it separate column space rather
    // than a true background layer — isBlock's actual purpose is preventing
    // other events from being scheduled in that range, not coexisting with
    // them, so it was the wrong mechanism). Reverted to plain appointments
    // only until a verified working approach is confirmed.
    return filteredAppointments;
  }, [schedulerEvents, sessionEvents, visibleProviderIds]);

  const toHourMinute = (value: Date) => {
    const hour = `${value.getHours()}`.padStart(2, '0');
    const minute = `${value.getMinutes()}`.padStart(2, '0');
    return `${hour}:${minute}`;
  };

  const schedulerResourceData = useMemo(
    () => providers
      .filter((provider) => visibleProviderIds.includes(provider.id))
      .map((provider) => {
        const providerSessions = sessionEvents.filter((session) => session.ProviderId === provider.id);

        if (providerSessions.length === 0) {
          return {
            id: provider.id,
            text: provider.name,
            color: 'rgba(59, 130, 246, 0.18)',
            workDays: [],
            startHour: '00:00',
            endHour: '00:00',
            hasSession: false,
          };
        }

        const starts = providerSessions.map((session) => new Date(session.StartTime));
        const ends = providerSessions.map((session) => new Date(session.EndTime));

        const earliestStart = starts.reduce((earliest, current) => (current < earliest ? current : earliest), starts[0]);
        const latestEnd = ends.reduce((latest, current) => (current > latest ? current : latest), ends[0]);

        const workDays = Array.from(
          new Set(providerSessions.map((session) => new Date(session.StartTime).getDay())),
        );

        return {
          id: provider.id,
          text: provider.name,
          color: providerSessions[0].CategoryColor || 'rgba(59, 130, 246, 0.18)',
          workDays: workDays,
          startHour: toHourMinute(earliestStart),
          endHour: toHourMinute(latestEnd),
          hasSession: true,
        };
      }),
    [providers, visibleProviderIds, sessionEvents],
  );

  const sessionWindowsByProvider = useMemo(() => {
    // Cover the full visible range (1 day in Day view, 7 days in Week view)
    const rangeStart = new Date(selectedDate);
    rangeStart.setHours(0, 0, 0, 0);
    if (currentView === 'Week') {
      rangeStart.setDate(rangeStart.getDate() - rangeStart.getDay());
    }
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setDate(rangeStart.getDate() + (currentView === 'Week' ? 7 : 1));

    const windows = new Map<string, Array<{ start: number; end: number; color: string }>>();

    sessionEvents.forEach((session) => {
      if (!session.ProviderId) return;
      const start = new Date(session.StartTime).getTime();
      const end = new Date(session.EndTime).getTime();
      if (Number.isNaN(start) || Number.isNaN(end)) return;
      if (end <= rangeStart.getTime() || start >= rangeEnd.getTime()) return;

      const clippedStart = Math.max(start, rangeStart.getTime());
      const clippedEnd = Math.min(end, rangeEnd.getTime());
      const existing = windows.get(session.ProviderId) || [];

      existing.push({
        start: clippedStart,
        end: clippedEnd,
        color: session.CategoryColor || 'rgba(59, 130, 246, 0.18)',
      });

      windows.set(session.ProviderId, existing);
    });

    return windows;
  }, [sessionEvents, selectedDate, currentView]);

  const toggleProviderVisibility = (providerId: string) => {
    setVisibleProviderIds((previous) => (
      previous.includes(providerId)
        ? previous.filter((id) => id !== providerId)
        : [...previous, providerId]
    ));
  };

  const showAllProviders = () => {
    setVisibleProviderIds(providers.map((provider) => provider.id));
  };

  const hideAllProviders = () => {
    setVisibleProviderIds([]);
  };

  useEffect(() => {
    if (!token) return;
    loadAppointments();
  }, [selectedDate, currentView, token]);

  const slotCount = useMemo(() => {
    if (slotMinutes === 10) return 6;
    if (slotMinutes === 15) return 4;
    if (slotMinutes === 30) return 2;
    return 1;
  }, [slotMinutes]);

  const navigateDateByView = (direction: -1 | 1) => {
    const updated = new Date(selectedDate);
    if (currentView === 'Week') {
      updated.setDate(updated.getDate() + 7 * direction);
    } else {
      updated.setDate(updated.getDate() + direction);
    }
    setSelectedDate(updated);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      scheduled: 'badge-ghost',
      in_waiting_room: 'badge-info',
      in_consult: 'badge-primary',
      completed: 'badge-success',
      cancelled: 'badge-error',
      no_show: 'badge-warning',
    };
    return `badge ${statusMap[status as keyof typeof statusMap] || 'badge-ghost'}`;
  };

  const onMiniCalendarChange = (args: ChangedEventArgs) => {
    if (args.value) {
      setSelectedDate(new Date(args.value as Date));
    }
  };

  const onEventRendered = (args: EventRenderedArgs) => {
    const event = args.data as SchedulerEvent;
    if (args.element && event?.Type === 'session') {
      args.element.classList.add('te-session-block');
      // Set !important so this wins over the stylesheet's own !important rule
      // for .te-session-block (which intentionally leaves background-color
      // unset so each session's actual color — not a fixed theme color —
      // shows here).
      args.element.style.setProperty('background-color', event.CategoryColor || 'rgba(59, 130, 246, 0.18)', 'important');
      args.element.style.borderColor = 'transparent';
      args.element.style.borderRadius = '0';
      args.element.style.zIndex = '1';
      args.element.style.pointerEvents = 'none';

      const facilityCode = getFacilityCode(event.FacilityName);
      const locationText = event.RoomName ? `${facilityCode} - ${event.RoomName}` : facilityCode;
      let locationLabel = args.element.querySelector('.te-session-location-label') as HTMLSpanElement | null;
      if (!locationLabel) {
        locationLabel = document.createElement('span');
        locationLabel.className = 'te-session-location-label';
        args.element.appendChild(locationLabel);
      }
      locationLabel.textContent = locationText;
      return;
    }

    if (args.element && event?.CategoryColor) {
      args.element.style.backgroundColor = event.CategoryColor;
      args.element.style.borderColor = event.CategoryColor;
      args.element.style.zIndex = '2';

      const { label, color } = getStatusIndicator(event.Status);
      let indicator = args.element.querySelector('.te-appointment-status-indicator') as HTMLSpanElement | null;

      if (!indicator) {
        indicator = document.createElement('span');
        indicator.className = 'te-appointment-status-indicator';
        args.element.appendChild(indicator);
      }

      indicator.textContent = label;
      indicator.style.backgroundColor = color;
      indicator.title = `Status: ${event.Status || 'scheduled'}`;

      // Details popup shows on a 1s hover instead of a click (see onEventClick,
      // which cancels the default click-triggered popup). Bound once per
      // element via the dataset flag -- onEventRendered can otherwise fire
      // again for an already-bound element and stack duplicate listeners.
      if (!args.element.dataset.teHoverBound) {
        args.element.dataset.teHoverBound = 'true';
        let hoverTimer: number | undefined;
        args.element.addEventListener('mouseenter', () => {
          hoverTimer = window.setTimeout(() => {
            scheduleRef.current?.openQuickInfoPopup(event as unknown as Record<string, any>);
          }, 1000);
        });
        args.element.addEventListener('mouseleave', () => {
          if (hoverTimer) window.clearTimeout(hoverTimer);
        });
      }
    }
  };

  const onEventClick = (args: EventClickArgs) => {
    const eventData = args.event as any;
    // Details popup is now hover-triggered (see onEventRendered above), not
    // click-triggered -- suppress Syncfusion's default click-opens-popup
    // behavior while keeping the row-selection side effect below.
    args.cancel = true;
    if (eventData?.Type === 'session' || eventData?.IsBlock) {
      return;
    }

    const appointmentId = String(eventData?.Id ?? eventData?.id ?? '');
    if (appointmentId) {
      setSelectedAppointmentId(appointmentId);
    }
  };

  // Syncfusion's default QuickInfo popup (a plain left-click on an
  // appointment) ships its own Edit/Delete buttons that normally open the
  // built-in editor -- but readonly={true} on the Schedule blocks that
  // transition entirely, so those buttons render and silently do nothing.
  // Intercept them here: Edit redirects to the same BookAppointmentModal
  // flow the right-click context menu already uses; the "Delete" button is
  // relabelled to Cancel and soft-cancels (status -> 'cancelled') rather
  // than deleting the row, so the appointment is retained for records but
  // hidden from the calendar (see filteredSchedulerEvents). A "Move" button
  // is injected alongside them, opening MoveAppointmentModal.
  const onPopupOpen = (args: PopupOpenEventArgs) => {
    if (args.type !== 'QuickInfo') return;

    const data = args.data as any;
    const appointmentId = String(data?.Id ?? '');
    if (!appointmentId) return;

    const editButton = args.element.querySelector('.e-event-edit');
    editButton?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scheduleRef.current?.closeQuickInfoPopup();
      setContextMenuAppointment(null);
      setEditingAppointmentId(appointmentId);
      setIsBookingModalOpen(true);
    }, { once: true });

    const cancelButton = args.element.querySelector('.e-event-delete');
    if (cancelButton) {
      cancelButton.textContent = 'Cancel';
      cancelButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        scheduleRef.current?.closeQuickInfoPopup();
        if (!window.confirm('Cancel this appointment? It will be removed from the calendar but kept on record.')) return;
        try {
          const response = await apiCall(`/scheduling/appointments/${appointmentId}/`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'cancelled' }),
          }, token || undefined);
          if (response.ok) {
            loadAppointments();
          } else {
            window.alert('Failed to cancel appointment');
          }
        } catch (err) {
          console.error('Error cancelling appointment:', err);
          window.alert('Failed to cancel appointment');
        }
      }, { once: true });

      const moveButton = document.createElement('button');
      moveButton.type = 'button';
      moveButton.className = cancelButton.className.replace('e-event-delete', 'te-event-move');
      moveButton.textContent = 'Move';
      moveButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        scheduleRef.current?.closeQuickInfoPopup();
        const startTime = data?.StartTime ? new Date(data.StartTime) : null;
        const endTime = data?.EndTime ? new Date(data.EndTime) : null;
        if (!startTime || !endTime) return;
        const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
        setMovingAppointment({
          id: appointmentId,
          providerId: String(data?.DoctorId ?? ''),
          startTime,
          durationMinutes: durationMinutes > 0 ? durationMinutes : 15,
        });
        setIsMoveModalOpen(true);
      }, { once: true });
      cancelButton.insertAdjacentElement('afterend', moveButton);
    }
  };

  // Session/background blocks aren't real appointments -- never draggable.
  const onDragStart = (args: DragEventArgs) => {
    const data = args.data as any;
    if (data?.Type === 'session' || data?.IsBlock) {
      args.cancel = true;
    }
  };

  // Drag-and-drop reschedule -- same day/week to a different time, and/or
  // across to a different provider's column. Syncfusion computes the
  // proposed new position for us (args.startTime/endTime/groupIndex); this
  // only ever confirms then persists it via the real API, mirroring
  // MoveAppointmentModal's PATCH exactly -- a declined confirmation cancels
  // the drop so Syncfusion snaps the appointment back to where it was.
  const onDragStop = async (args: DragEventArgs) => {
    const data = args.data as any;
    const appointmentId = String(data?.Id ?? '');
    if (!appointmentId || !args.startTime || !args.endTime || !scheduleRef.current) return;

    const groupIndex = args.groupIndex;
    const resource = typeof groupIndex === 'number' ? scheduleRef.current.getResourcesByIndex(groupIndex) : null;
    const newProviderId = (resource?.resourceData as { id?: string } | undefined)?.id ?? String(data?.DoctorId ?? '');
    const newProviderName = providers.find((p) => p.id === newProviderId)?.name || 'the same provider';

    const confirmed = window.confirm(
      `Move this appointment to ${args.startTime.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} with ${newProviderName}?`
    );
    if (!confirmed) {
      args.cancel = true;
      return;
    }

    try {
      const response = await apiCall(`/scheduling/appointments/${appointmentId}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          provider: newProviderId,
          start_time: args.startTime.toISOString(),
          end_time: args.endTime.toISOString(),
          provider_session: null,
        }),
      }, token || undefined);

      if (!response.ok) {
        window.alert('Failed to move appointment');
      }
    } catch (err) {
      console.error('Error moving appointment:', err);
      window.alert('Failed to move appointment');
    } finally {
      // args.cancel has no effect here -- dragStop's synchronous portion has
      // already returned by the time this await resolves, so Syncfusion has
      // already committed the drop optimistically regardless of outcome.
      // Reload unconditionally so the calendar reflects real server state
      // either way (a failed PATCH gets visually reverted by the refetch,
      // not by cancelling the drag).
      loadAppointments();
      loadSchedulerEvents();
    }
  };

  // Right-click "New Appointment" / "Edit Appointment" -- Syncfusion's
  // documented pattern for a Schedule context menu (getCellDetails/
  // getEventDetails/getResourcesByIndex on the right-clicked DOM element).
  // Built-in add/edit/delete stay off via eventSettings -- this only ever opens our
  // own BookAppointmentModal, never Syncfusion's built-in editor.
  const contextMenuItems: MenuItemModel[] = useMemo(() => [
    { text: 'New Appointment', iconCss: 'e-icons new', id: 'Add' },
    { text: 'Edit Appointment', iconCss: 'e-icons edit', id: 'Edit' },
    { text: 'Move Appointment', iconCss: 'e-icons drag', id: 'Move' },
  ], []);

  const onContextMenuBeforeOpen = (args: BeforeOpenCloseMenuEventArgs) => {
    const eventTarget = (args.event as MouseEvent)?.target as HTMLElement | null;
    if (!eventTarget || closest(eventTarget, '.e-contextmenu')) {
      return;
    }

    const target = closest(eventTarget, '.e-appointment,.e-work-cells,.e-work-hours');
    if (isNullOrUndefined(target)) {
      args.cancel = true;
      return;
    }
    contextMenuTargetRef.current = target;

    if (target.classList.contains('e-appointment')) {
      contextMenuRef.current?.hideItems(['Add'], true);
      contextMenuRef.current?.showItems(['Edit', 'Move'], true);
    } else {
      contextMenuRef.current?.hideItems(['Edit', 'Move'], true);
      contextMenuRef.current?.showItems(['Add'], true);
    }
  };

  const onContextMenuItemSelect = (args: MenuEventArgs) => {
    const target = contextMenuTargetRef.current;
    if (!target || !scheduleRef.current) return;

    if (args.item.id === 'Add') {
      const cellDetails = scheduleRef.current.getCellDetails(target) as CellClickEventArgs;
      const groupIndex = cellDetails.groupIndex;
      const resource = typeof groupIndex === 'number' ? scheduleRef.current.getResourcesByIndex(groupIndex) : null;
      const providerId = (resource?.resourceData as { id?: string } | undefined)?.id;
      if (!providerId || !cellDetails.startTime) return;

      setEditingAppointmentId(null);
      setContextMenuAppointment({ providerId: String(providerId), startTime: cellDetails.startTime });
      setIsBookingModalOpen(true);
    } else if (args.item.id === 'Edit') {
      const eventData = scheduleRef.current.getEventDetails(target) as any;
      const appointmentId = String(eventData?.Id ?? '');
      if (!appointmentId) return;

      setContextMenuAppointment(null);
      setEditingAppointmentId(appointmentId);
      setIsBookingModalOpen(true);
    } else if (args.item.id === 'Move') {
      const eventData = scheduleRef.current.getEventDetails(target) as any;
      const appointmentId = String(eventData?.Id ?? '');
      const startTime = eventData?.StartTime ? new Date(eventData.StartTime) : null;
      const endTime = eventData?.EndTime ? new Date(eventData.EndTime) : null;
      if (!appointmentId || !startTime || !endTime) return;

      const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
      setMovingAppointment({
        id: appointmentId,
        providerId: String(eventData?.DoctorId ?? ''),
        startTime,
        durationMinutes: durationMinutes > 0 ? durationMinutes : 15,
      });
      setIsMoveModalOpen(true);
    }
  };

  // Same class-driven-by-data-group-index technique as onStaticRenderCell
  // (confirmed working) — color is set as a plain inline background-color
  // (matching the pattern already proven reliable for appointment fills)
  // instead of a --te-session-cell-color custom property + color-mix(),
  // which never actually worked despite looking correct. Per-time-window:
  // looks up the specific session covering this cell's time (sessionWindowsByProvider)
  // rather than one flat color for the provider's whole day, so e.g. a
  // morning "New Consults" session, a "Lunch" break, and an afternoon "OR"
  // session on the same day can each show their own color.
  const onRenderCell = (args: RenderCellEventArgs) => {
    if (!args.element || !args.date) return;
    if (!args.element.classList.contains('e-work-cells') && !args.element.classList.contains('e-work-hours')) return;

    const element = args.element as HTMLElement;

    const groupIndexRaw = element.getAttribute('data-group-index');
    if (groupIndexRaw == null) return;

    const groupIndex = Number.parseInt(groupIndexRaw, 10);
    if (Number.isNaN(groupIndex)) return;

    const resource = schedulerResourceData[groupIndex] as { id?: string; hasSession?: boolean } | undefined;
    if (!resource?.id || !resource.hasSession) {
      element.classList.remove('te-session-work-cell');
      element.style.removeProperty('background-color');
      return;
    }

    const windows = sessionWindowsByProvider.get(resource.id);
    if (!windows || windows.length === 0) {
      element.classList.remove('te-session-work-cell');
      element.style.removeProperty('background-color');
      return;
    }

    // Point-in-time check (cell start falls inside [window.start, window.end))
    // rather than computing a synthetic cell-end from slotCount and checking
    // for range overlap — fewer moving parts, no dependency on slotCount
    // actually matching what Syncfusion gives us for this cell.
    const cellStart = new Date(args.date as Date).getTime();
    const activeWindow = windows.find((w) => cellStart >= w.start && cellStart < w.end);

    if (!activeWindow) {
      element.classList.remove('te-session-work-cell');
      element.style.removeProperty('background-color');
      return;
    }

    addClass([element], ['te-session-work-cell', `te-session-group-${groupIndex}`]);
    element.style.setProperty(
      'background-color',
      activeWindow.color.startsWith('#') ? hexToRgba(activeWindow.color, 0.16) : activeWindow.color,
      'important',
    );
  };

  const selectedAppointment = selectedAppointmentId
    ? appointments.find((apt) => apt.id === selectedAppointmentId)
    : appointments[0];

  const upcomingAppointments = appointments.slice(0, 6);

  const recentEvents = useMemo(() => {
    if (!selectedAppointment) return [];
    const baseEvents = [
      { label: 'Appointment selected in scheduler', at: `${selectedAppointment.start_time}` },
      { label: `Status: ${selectedAppointment.status.replace(/_/g, ' ')}`, at: 'Current' },
    ];

    if (selectedAppointment.notes || selectedAppointment.admin_notes) {
      baseEvents.push({ label: 'Admin note present', at: 'Today' });
    }

    return baseEvents;
  }, [selectedAppointment]);

  useEffect(() => {
    if (appointments.length === 0) {
      setSelectedAppointmentId(null);
      return;
    }

    if (!selectedAppointmentId || !appointments.some((apt) => apt.id === selectedAppointmentId)) {
      setSelectedAppointmentId(appointments[0].id);
    }
  }, [appointments, selectedAppointmentId]);

  useEffect(() => {
    if (!selectedAppointment?.id) {
      setTodoItems([]);
      return;
    }

    const key = `appointment-sidebar-todos-${selectedAppointment.id}`;
    const stored = localStorage.getItem(key);

    if (stored) {
      try {
        setTodoItems(JSON.parse(stored));
        return;
      } catch {
        localStorage.removeItem(key);
      }
    }

    const defaults: AppointmentTodo[] = [
      { id: `pif-${selectedAppointment.id}`, text: 'PIF completed?', category: 'PIF', completed: false },
      {
        id: `ifc-${selectedAppointment.id}`,
        text: 'IFC completed?',
        category: 'IFC',
        completed: !/procedure/i.test(selectedAppointment.appointment_type || ''),
      },
    ];
    setTodoItems(defaults);
  }, [selectedAppointment]);

  useEffect(() => {
    if (!selectedAppointment?.id) return;
    const key = `appointment-sidebar-todos-${selectedAppointment.id}`;
    localStorage.setItem(key, JSON.stringify(todoItems));
  }, [todoItems, selectedAppointment]);

  const addTodoItem = () => {
    const cleanText = todoText.trim();
    if (!cleanText) return;

    setTodoItems((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        text: cleanText,
        category: todoCategory,
        completed: false,
      },
    ]);
    setTodoText('');
  };

  const toggleTodo = (id: string) => {
    setTodoItems((prev) => prev.map((item) => (item.id === id ? { ...item, completed: !item.completed } : item)));
  };

  const removeTodo = (id: string) => {
    setTodoItems((prev) => prev.filter((item) => item.id !== id));
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  return (
    <div className="flex h-full">
      {/* Main scrollable content */}
      <div className="flex-1 overflow-auto p-4 min-w-0">
      <div className="mb-3 flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
            <FaCalendarAlt className="text-primary" />
            Appointments
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          <button className="btn btn-primary gap-2" onClick={() => {
            setEditingAppointmentId(null);
            setContextMenuAppointment(null);
            setIsBookingModalOpen(true);
          }}>
            <FaPlus /> New Appointment
          </button>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaCalendarCheck className="text-primary text-lg" />
            <span className="text-xs">Total:</span>
            <span className="text-lg font-bold">{stats.total}</span>
          </div>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaClock className="text-info text-lg" />
            <span className="text-xs">Scheduled:</span>
            <span className="text-lg font-bold">{stats.scheduled}</span>
          </div>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaUserClock className="text-warning text-lg" />
            <span className="text-xs">In Progress:</span>
            <span className="text-lg font-bold">{stats.in_progress}</span>
          </div>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaCheckCircle className="text-success text-lg" />
            <span className="text-xs">Completed:</span>
            <span className="text-lg font-bold text-success">{stats.completed}</span>
          </div>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaTimesCircle className="text-error text-lg" />
            <span className="text-xs">Cancelled:</span>
            <span className="text-lg font-bold text-error">{stats.cancelled}</span>
          </div>
          <div className="bg-base-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <FaUsers className="text-warning text-lg" />
            <span className="text-xs">No Show:</span>
            <span className="text-lg font-bold">{stats.no_show}</span>
          </div>
        </div>
      </div>

      <div className="card bg-base-200 shadow mb-3">
        <div className="card-body p-3">
          <div className="flex flex-col gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => navigateDateByView(-1)} className="btn btn-outline btn-sm"><FaChevronLeft /></button>
                  <button onClick={() => navigateDateByView(1)} className="btn btn-outline btn-sm"><FaChevronRight /></button>
                  <button onClick={goToToday} className="btn btn-sm">today</button>
                </div>
                <h2 className="text-lg font-semibold text-base-content">{headerTitle}</h2>
                <div className="flex items-center gap-2 ml-auto">
                  <button onClick={() => navigate('/scheduling/waitingroom')} className="btn btn-secondary btn-sm">
                    <FaUsers className="mr-2" />
                    Waiting Room
                  </button>
                  <button onClick={() => navigate('/scheduling/waitinglist')} className="btn btn-outline btn-sm">
                    <FaClock className="mr-2" />
                    Waiting List
                  </button>
                  <div className="join">
                    <select
                      className="select select-bordered join-item"
                      value={slotMinutes}
                      onChange={(event) => setSlotMinutes(Number(event.target.value) as 10 | 15 | 30 | 60)}
                      title="Time interval"
                    >
                      <option value={10}>10 min</option>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                    </select>
                    <button className={`btn join-item ${currentView === 'Day' ? 'btn-primary' : ''}`} onClick={() => setCurrentView('Day')}>day</button>
                    <button className={`btn join-item ${currentView === 'Week' ? 'btn-primary' : ''}`} onClick={() => setCurrentView('Week')}>week</button>
                  </div>
                </div>
              </div>

              {sessionEvents.length === 0 && (
                <div className="mb-2 text-xs text-warning">
                  No session records loaded for this view range, so no session background shading can be applied in the top scheduler.
                </div>
              )}

              {/* Stable, always-present wrapper for the ContextMenu's `target` selector --
                  ScheduleComponent itself only exists in the DOM once `loading` is false, and
                  gets a fresh `key` remount below (see comment on ScheduleComponent), so
                  targeting its own .te-appointments-scheduler class directly crashes the
                  ContextMenu ("Cannot use 'in' operator ... in undefined") whenever it mounts
                  before/between those. This div is never removed by either of those. */}
              <div className="te-scheduler-context-target">
              {loading ? (
                <div className="flex justify-center items-center min-h-[620px] h-[calc(100vh-300px)]"><span className="loading loading-spinner loading-lg"></span></div>
              ) : (
                <div className="min-h-[620px] h-[calc(100vh-300px)] overflow-hidden rounded-md border border-base-300 bg-base-100">
                  <ScheduleComponent
                    // Forces a clean remount whenever the displayed date/view changes or
                    // session data for it finishes loading. renderCell (which paints the
                    // session background tint) runs during Syncfusion's own internal
                    // render pass — it doesn't automatically re-run just because
                    // sessionWindowsByProvider's closure value changed after an async
                    // fetch resolves later. loadSessionEvents fetches asynchronously on
                    // both initial mount and every date/view navigation, so without this
                    // the renderCell pass that actually runs sees stale (often empty)
                    // session data and paints nothing, permanently, until the next
                    // remount.
                    key={`${selectedDate.toDateString()}-${currentView}-${sessionEvents.length}`}
                    ref={scheduleRef}
                    height="100%"
                    width="100%"
                    selectedDate={selectedDate}
                    currentView={currentView}
                    timezone={browserTimeZone}
                    startHour="06:00"
                    endHour="22:00"
                    workHours={{ highlight: true }}
                    timeScale={{ enable: true, interval: 60, slotCount }}
                    group={{ resources: ['Doctors'], byDate: false }}
                    showHeaderBar={false}
                    renderCell={onRenderCell}
                    cssClass={`te-appointments-scheduler ${currentView === 'Day' ? 'te-day-view' : 'te-week-view'}`}
                    eventSettings={{
                      dataSource: filteredSchedulerEvents,
                      fields: {
                        id: 'Id',
                        subject: { name: 'Subject' },
                        startTime: { name: 'StartTime' },
                        endTime: { name: 'EndTime' },
                        resourceId: { name: 'DoctorId' },
                        isBlock: { name: 'IsBlock' },
                        isReadonly: { name: 'IsReadonly' },
                        cssClass: { name: 'CssClass' },
                      },
                      // Built-in add/edit/delete stay off -- those only ever
                      // happen through our own modals (see popupOpen/context
                      // menu above). readonly is now false only to allow
                      // dragging; these three flags keep everything else the
                      // blanket readonly={true} used to block still blocked.
                      allowAdding: false,
                      allowEditing: false,
                      allowDeleting: false,
                    }}
                    eventRendered={onEventRendered}
                    eventClick={onEventClick}
                    popupOpen={onPopupOpen}
                    dragStart={onDragStart}
                    dragStop={onDragStop}
                    readonly={false}
                    allowDragAndDrop={true}
                    allowResizing={false}
                  >
                    <ResourcesDirective>
                      <ResourceDirective
                        field="DoctorId"
                        title="Doctor"
                        name="Doctors"
                        dataSource={schedulerResourceData}
                        textField="text"
                        idField="id"
                        colorField="color"
                        workDaysField="workDays"
                        startHourField="startHour"
                        endHourField="endHour"
                      />
                    </ResourcesDirective>
                    <Inject services={[Day, Week]} />
                  </ScheduleComponent>
                </div>
              )}
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="card bg-base-200 shadow">
        <div className="card-body">
          <h2 className="card-title mb-4">
            <FaChartLine className="text-primary" />
            Appointment Details
          </h2>

          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Patient</th>
                  <th>MRN</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Room</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((apt) => (
                  <tr
                    key={apt.id}
                    className={`hover cursor-pointer ${selectedAppointment?.id === apt.id ? 'bg-base-300' : ''}`}
                    onClick={() => setSelectedAppointmentId(apt.id)}
                  >
                    <td className="font-mono">{formatLocalTime(apt.start_time)} - {formatLocalTime(apt.end_time)}</td>
                    <td className="font-bold">{apt.patient_name}</td>
                    <td className="text-sm opacity-60">{apt.patient_mrn}</td>
                    <td>{apt.provider_name}</td>
                    <td>{apt.appointment_type}</td>
                    <td>{apt.room_name || '-'}</td>
                    <td>
                      <span className={getStatusBadge(apt.status)}>{apt.status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      </div>

      {/* Calendar & Patient sidebar — layout flex element, no fixed positioning */}
      <div className={`flex-shrink-0 overflow-hidden transition-all duration-300 bg-base-100 border-l border-base-300 ${showSidebar ? 'w-80' : 'w-0'}`}>
      <div className="w-80 h-full overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-base-content">Calendar & Patient</h2>
          </div>

          <div className="flex justify-center mb-4">
            <div className="card bg-base-200 border border-base-300 w-fit overflow-hidden">
              <div className="card-body p-2 flex justify-center">
                <CalendarComponent
                  value={selectedDate}
                  change={onMiniCalendarChange}
                  cssClass="te-mini-calendar"
                />
              </div>
            </div>
          </div>

          <div className="card bg-base-200 shadow-sm mb-3">
            <div className="card-body p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <FaUserMd className="text-primary" />
                  Doctors
                </h3>
                <div className="flex items-center gap-1">
                  <button className="btn btn-ghost btn-xs" onClick={showAllProviders}>All</button>
                  <button className="btn btn-ghost btn-xs" onClick={hideAllProviders}>None</button>
                </div>
              </div>

              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto pr-1">
                {providers.length === 0 ? (
                  <p className="text-xs opacity-60">No providers available.</p>
                ) : (
                  providers.map((provider) => {
                    const checked = visibleProviderIds.includes(provider.id);
                    return (
                      <label key={provider.id} className="label cursor-pointer justify-start gap-2 py-1">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs checkbox-primary"
                          checked={checked}
                          onChange={() => toggleProviderVisibility(provider.id)}
                        />
                        <span className="label-text text-xs truncate">{provider.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {selectedAppointment ? (
            <div className="space-y-3">
              <div className="card bg-base-200 shadow-sm">
                <div className="card-body p-3">
                  <h3 className="font-bold text-base-content">{selectedAppointment.patient_name}</h3>
                  <p className="text-xs opacity-70">MRN: {selectedAppointment.patient_mrn || 'N/A'}</p>
                  <div className="divider my-1" />
                  <p className="text-sm"><span className="font-semibold">Time:</span> {formatLocalTime(selectedAppointment.start_time)} - {formatLocalTime(selectedAppointment.end_time)}</p>
                  <p className="text-xs opacity-70"><span className="font-semibold">Local:</span> {formatLocalDateTime(selectedAppointment.start_time)} - {formatLocalDateTime(selectedAppointment.end_time)}</p>
                  <p className="text-sm"><span className="font-semibold">Type:</span> {selectedAppointment.appointment_type}</p>
                  <p className="text-sm"><span className="font-semibold">Provider:</span> {selectedAppointment.provider_name}</p>
                  <p className="text-sm"><span className="font-semibold">Room:</span> {selectedAppointment.room_name || 'Unassigned'}</p>
                  <p className="text-sm"><span className="font-semibold">Status:</span> {selectedAppointment.status.replace(/_/g, ' ')}</p>
                  <div className="divider my-1" />
                  <p className="text-sm font-semibold">Clinic Admin Notes</p>
                  <p className="text-sm opacity-80">{selectedAppointment.admin_notes || selectedAppointment.notes || 'No admin notes recorded.'}</p>
                  <div className="divider my-1" />
                  <p className="text-sm">
                    <span className="font-semibold">Outstanding Payments:</span>{' '}
                    {formatCurrency(Number(selectedAppointment.outstanding_amount ?? selectedAppointment.outstanding_balance ?? 0))}
                  </p>
                  {selectedAppointment.patient_id && (
                    <div className="card-actions justify-end mt-2">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/patients/${selectedAppointment.patient_id}`)}
                      >
                        View Patient
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="card bg-base-200 shadow-sm">
                <div className="card-body p-3">
                  <h3 className="font-semibold text-sm mb-2">Upcoming Appointments</h3>
                  <div className="space-y-2">
                    {upcomingAppointments.map((apt) => (
                      <button
                        key={apt.id}
                        className="w-full text-left p-2 rounded hover:bg-base-300"
                        onClick={() => setSelectedAppointmentId(apt.id)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium truncate pr-2">{apt.patient_name}</span>
                          <span className="text-xs opacity-70">{apt.start_time}</span>
                        </div>
                        <div className="text-xs opacity-70 truncate">{apt.appointment_type}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card bg-base-200 shadow-sm">
                <div className="card-body p-3">
                  <h3 className="font-semibold text-sm mb-2">Recent Events</h3>
                  <ul className="space-y-2 mb-3">
                    {recentEvents.map((event, index) => (
                      <li key={`${event.label}-${index}`} className="text-sm border-l-2 border-primary/30 pl-2">
                        <div className="font-medium">{event.label}</div>
                        <div className="text-xs opacity-70">{event.at}</div>
                      </li>
                    ))}
                  </ul>

                  <div className="divider my-2" />

                  <h3 className="font-semibold text-sm mb-2">Patient Admin To-Do</h3>
                  <div className="space-y-2 mb-2 max-h-48 overflow-y-auto">
                    {todoItems.map((item) => (
                      <div key={item.id} className="flex items-start gap-2 p-2 rounded bg-base-100 border border-base-300">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs checkbox-primary mt-1"
                          checked={item.completed}
                          onChange={() => toggleTodo(item.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${item.completed ? 'line-through opacity-60' : ''}`}>{item.text}</div>
                          <div className="text-xs opacity-70">{item.category}</div>
                        </div>
                        <button className="btn btn-ghost btn-xs" onClick={() => removeTodo(item.id)}>
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={todoText}
                      onChange={(e) => setTodoText(e.target.value)}
                      className="input input-bordered input-sm flex-1"
                      placeholder="Add admin note or task"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <select
                      value={todoCategory}
                      onChange={(e) => setTodoCategory(e.target.value as AppointmentTodo['category'])}
                      className="select select-bordered select-sm flex-1"
                    >
                      <option value="PIF">PIF</option>
                      <option value="IFC">IFC</option>
                      <option value="Admin">Admin</option>
                      <option value="Billing">Billing</option>
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={addTodoItem}>
                      <FaPlus />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-base-content opacity-60 py-10">
              <FaUsers className="mx-auto text-3xl mb-2 opacity-40" />
              <p>No appointments to display</p>
            </div>
          )}
      </div>
      </div>

      <ContextMenuComponent
        ref={contextMenuRef}
        cssClass="te-scheduler-context-menu"
        target=".te-scheduler-context-target"
        items={contextMenuItems}
        beforeOpen={onContextMenuBeforeOpen}
        select={onContextMenuItemSelect}
      />

      <BookAppointmentModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setEditingAppointmentId(null);
          setContextMenuAppointment(null);
        }}
        onBooked={() => {
          loadAppointments();
          loadSchedulerEvents();
          setEditingAppointmentId(null);
          setContextMenuAppointment(null);
        }}
        providers={providers}
        defaultDate={selectedDate}
        initialProviderId={contextMenuAppointment?.providerId}
        initialStartTime={contextMenuAppointment?.startTime}
        editingAppointmentId={editingAppointmentId}
      />

      <MoveAppointmentModal
        isOpen={isMoveModalOpen}
        onClose={() => {
          setIsMoveModalOpen(false);
          setMovingAppointment(null);
        }}
        onMoved={() => {
          loadAppointments();
          loadSchedulerEvents();
        }}
        appointmentId={movingAppointment?.id ?? null}
        providers={providers}
        initialProviderId={movingAppointment?.providerId ?? ''}
        initialStartTime={movingAppointment?.startTime ?? null}
        initialDurationMinutes={movingAppointment?.durationMinutes ?? 15}
      />

    </div>
  );
};

export default AppointmentsDashboard;
